import { useEffect, useMemo, useRef, useState } from 'react';
import Fuse from 'fuse.js';
import { Search, X } from 'lucide-react';
import type { AppRecord } from '../lib/types';
import { useApi } from '../hooks/useApi';
import { cn } from '../lib/cn';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CommandPalette = ({ open, onClose }: Props) => {
  const { data: apps } = useApi<AppRecord[]>(open ? '/api/apps' : null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const fuse = useMemo(
    () =>
      new Fuse(apps ?? [], {
        keys: ['name', 'description', 'category'],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [apps],
  );

  const results = useMemo(() => {
    if (!apps) return [];
    if (!query.trim()) return apps.slice(0, 20);
    return fuse.search(query).slice(0, 20).map((r) => r.item);
  }, [apps, fuse, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Defer focus until the dialog is in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => setActiveIdx(0), [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const pick = results[activeIdx];
        if (pick) {
          window.open(pick.url, '_blank', 'noopener,noreferrer');
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, activeIdx, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 px-4 pt-[10vh] backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl border border-line bg-bg-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search apps..."
            className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-text-tertiary"
            aria-label="Search"
          />
          <button
            type="button"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded text-text-tertiary hover:bg-bg-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-text-tertiary">
              No matches.
            </div>
          ) : (
            results.map((app, i) => (
              <a
                key={app.id}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setActiveIdx(i)}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                  i === activeIdx ? 'bg-accent-pale text-accent' : 'text-text-primary',
                )}
              >
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-brand-navy-pale text-[9px] font-semibold text-brand-navy">
                  {app.name
                    .split(/\s+/)
                    .map((s) => s[0] ?? '')
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{app.name}</div>
                  {app.description && (
                    <div className="truncate text-xs text-text-tertiary">{app.description}</div>
                  )}
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-text-tertiary">
                  {app.category}
                </span>
              </a>
            ))
          )}
        </div>
        <div className="flex items-center justify-between border-t border-line bg-bg-secondary px-4 py-2 text-[11px] text-text-tertiary">
          <span>
            <kbd className="rounded border border-line bg-bg-primary px-1 py-0.5 font-mono">↑↓</kbd> navigate
            <span className="mx-2">·</span>
            <kbd className="rounded border border-line bg-bg-primary px-1 py-0.5 font-mono">↵</kbd> open
            <span className="mx-2">·</span>
            <kbd className="rounded border border-line bg-bg-primary px-1 py-0.5 font-mono">esc</kbd> close
          </span>
          <span>{results.length} match{results.length === 1 ? '' : 'es'}</span>
        </div>
      </div>
    </div>
  );
};
